import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Alert, CircularProgress, Grid, Divider
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import { getAllJobsAdmin, createJobAdmin, updateJobAdmin, deleteJobAdmin } from '../services/api';

import RichTextEditor from '../components/features/RichTextEditor';

const ManageJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    companyLogo: '',
    location: {
      type: 'offline',
      address: '',
      city: '',
      state: '',
      country: 'India',
      mapLink: ''
    },
    jobType: 'full-time',
    workType: 'remote',
    category: 'Engineering',
    salaryRange: '',
    description: '',
    applyUrl: '',
    lastDate: '',
    status: 'active'
  });

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await getAllJobsAdmin();
      if (res.success) {
        setJobs(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleOpen = (job: any = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        title: job.title || '',
        company: job.company || '',
        companyLogo: job.companyLogo || '',
        location: {
          type: job.location?.type || 'offline',
          address: job.location?.address || '',
          city: job.location?.city || '',
          state: job.location?.state || '',
          country: job.location?.country || 'India',
          mapLink: job.location?.mapLink || ''
        },
        jobType: job.jobType || 'full-time',
        workType: job.workType || 'remote',
        category: job.category || 'Engineering',
        salaryRange: job.salaryRange || '',
        description: job.description || '',
        applyUrl: job.applyUrl || '',
        lastDate: job.lastDate ? new Date(job.lastDate).toISOString().split('T')[0] : '',
        status: job.status || 'active'
      });
    } else {
      setEditingJob(null);
      setFormData({
        title: '',
        company: '',
        companyLogo: '',
        location: {
          type: 'offline',
          address: '',
          city: '',
          state: '',
          country: 'India',
          mapLink: ''
        },
        jobType: 'full-time',
        workType: 'remote',
        category: 'Engineering',
        salaryRange: '',
        description: '',
        applyUrl: '',
        lastDate: '',
        status: 'active'
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    try {
      if (editingJob) {
        await updateJobAdmin(editingJob._id, formData);
      } else {
        await createJobAdmin(formData);
      }
      fetchJobs();
      handleClose();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteJobAdmin(id);
        fetchJobs();
      } catch (err: any) {
        alert(err.response?.data?.message || 'Deletion failed');
      }
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 
    'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi'
  ];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700} color="primary">Manage Jobs & Careers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>Post New Job</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><b>Job / Company</b></TableCell>
              <TableCell><b>Category</b></TableCell>
              <TableCell><b>Details</b></TableCell>
              <TableCell><b>Last Date</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell align="right"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No job opportunities posted yet.</TableCell>
              </TableRow>
            ) : jobs.map((job) => (
              <TableRow key={job._id}>
                <TableCell>
                   <Box display="flex" alignItems="center" gap={2}>
                     {job.companyLogo && <img src={job.companyLogo} alt="logo" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'contain' }} />}
                     <Box>
                       <Typography variant="subtitle2" fontWeight={700}>{job.title}</Typography>
                       <Typography variant="caption" color="textSecondary">{job.company}</Typography>
                     </Box>
                   </Box>
                </TableCell>
                <TableCell>{job.category}</TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    <Chip label={job.jobType} size="small" variant="outlined" />
                    <Chip label={job.workType} size="small" variant="outlined" color="primary" />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {job.lastDate ? new Date(job.lastDate).toLocaleDateString() : 'No Limit'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={job.status || 'Active'} 
                    size="small" 
                    color={job.status === 'expired' || job.status === 'closed' ? 'secondary' : 'success'} 
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton color="primary" size="small" onClick={() => handleOpen(job)}><EditIcon /></IconButton>
                  <IconButton color="error" size="small" onClick={() => handleDelete(job._id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          {editingJob ? 'Update Opportunity' : 'Post New Opportunity'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid size={{ xs: 12 }}><Typography variant="h6" color="primary">Basic Information</Typography></Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="Job Title" fullWidth value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="Company Name" fullWidth value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="Company Logo URL" fullWidth value={formData.companyLogo} onChange={e => setFormData({...formData, companyLogo: e.target.value})} placeholder="https://logo-url.com" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="Apply Last Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.lastDate} onChange={e => setFormData({...formData, lastDate: e.target.value})} />
            </Grid>

            <Grid size={{ xs: 12 }}><Typography variant="h6" color="primary">Job Details</Typography></Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select label="Category" fullWidth value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                {['Engineering', 'Design', 'Product', 'Marketing', 'Sales', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select label="Job Type" fullWidth value={formData.jobType} onChange={e => setFormData({...formData, jobType: e.target.value})}>
                {[
                  {val: 'full-time', lab: 'Full-time'}, 
                  {val: 'part-time', lab: 'Part-time'}, 
                  {val: 'internship', lab: 'Internship'}, 
                  {val: 'contract', lab: 'Contract'}, 
                  {val: 'referral', lab: 'Referral'}
                ].map(t => <MenuItem key={t.val} value={t.val}>{t.lab}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select label="Work Type" fullWidth value={formData.workType} onChange={e => setFormData({...formData, workType: e.target.value})}>
                {[
                  {val: 'remote', lab: 'Remote'}, 
                  {val: 'onsite', lab: 'On-site'}, 
                  {val: 'hybrid', lab: 'Hybrid'}, 
                  {val: 'offcampus', lab: 'Off-campus'}
                ].map(t => <MenuItem key={t.val} value={t.val}>{t.lab}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="Salary Range" fullWidth value={formData.salaryRange} onChange={e => setFormData({...formData, salaryRange: e.target.value})} placeholder="e.g. ₹6LPA - ₹12LPA" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField select label="Posting Status" fullWidth value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                {['active', 'expired', 'closed', 'draft'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Location Section */}
            <Grid size={{ xs: 12 }}><Typography variant="h6" color="primary">Location Details</Typography></Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select label="Location Type" fullWidth value={formData.location.type} onChange={e => setFormData({...formData, location: {...formData.location, type: e.target.value as any}})}>
                {['online', 'offline', 'hybrid'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label="Country" fullWidth value={formData.location.country} onChange={e => setFormData({...formData, location: {...formData.location, country: e.target.value}})} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              {formData.location.country.toLowerCase() === 'india' ? (
                <TextField select label="State" fullWidth value={formData.location.state} onChange={e => setFormData({...formData, location: {...formData.location, state: e.target.value}})}>
                  {indianStates.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              ) : (
                <TextField label="State/Province" fullWidth value={formData.location.state} onChange={e => setFormData({...formData, location: {...formData.location, state: e.target.value}})} />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="City" fullWidth value={formData.location.city} onChange={e => setFormData({...formData, location: {...formData.location, city: e.target.value}})} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label="Exact Address / Venue" fullWidth value={formData.location.address} onChange={e => setFormData({...formData, location: {...formData.location, address: e.target.value}})} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Google Maps Link" fullWidth value={formData.location.mapLink} onChange={e => setFormData({...formData, location: {...formData.location, mapLink: e.target.value}})} placeholder="https://maps.google.com/..." />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Apply URL" fullWidth value={formData.applyUrl} onChange={e => setFormData({...formData, applyUrl: e.target.value})} placeholder="https://company.career/apply..." />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Job Description
              </Typography>
              <RichTextEditor 
                value={formData.description} 
                onChange={val => setFormData({...formData, description: val})} 
                placeholder="Describe responsibilities, requirements, and about company..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit">Cancel</Button>
          <Button variant="contained" size="large" onClick={handleSubmit} sx={{ px: 4 }}>
            {editingJob ? 'Update Posting' : 'Post Opportunity'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageJobsPage;
